// ---------------------------------------------------------------------------
// YouAreYou (uru) — ContactDetailModal ("Our Trust" view)
//
// Unified 3-col CSS grid: [60px DATA TYPE] [1fr I SHARE / You card] [1fr THEY SHARE / Contact card]
// Both contact cards are 1fr each → equal width, symmetric.
// Trust icon is absolutely positioned at the col-2/col-3 boundary.
// "Add/Remove Trust Factors" bar is a standard accordion (text left, chevron right).
// When Share/Grant is clicked the I SHARE cell shows the actual value being sent;
// the single-line controls (mode · expiry · Send/Cancel) appear as col-span-3 below.
// Pending requested fields are previewed inside the contact card at the top.
// ---------------------------------------------------------------------------

import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  getConnection,
  getUruMe,
  cancelConnection,
  requestDataSharing,
  grantDataSharing,
  revokeDataSharing,
} from "@/api/endpoints";
import type {
  Connection,
  ConnectionPerson,
  SharableField,
  SharingMode,
  MySharingEntry,
  TheirSharingEntry,
} from "@/types/api";
import { resolveAvatarUrl } from "@/types/api";
import { useAuth } from "@/hooks/useAuth";
import Icon from "@/components/ui/Icon";
import { cn, getInitials } from "@/lib/utils";
import { useContactMatch } from "@/hooks/useContactMatch";
import { isNative } from "@/lib/platform";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ContactDetailModalProps {
  connectionId: number;
  onClose: () => void;
  onCancelled?: (pk: number) => void;
  onSharingLoaded?: (connectionId: number, hasActiveSharing: boolean) => void;
}

interface ContactInfo {
  fullName: string;
  initials: string;
  avatarUrl: string | null;
  phone: string | null;
}

interface MyInfo {
  fullName: string;
  initials: string;
  avatarUrl: string | null;
  phone?: string;
  email?: string;
  /** Per-factor KYC completion flags. key = SharableField. */
  kyc?: Partial<Record<string, boolean>>;
}

type ModalState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "resolved"; connection: Connection; contact: ContactInfo };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SHARABLE_FIELDS: Array<{
  field: SharableField; label: string; short: string; icon: string;
  /** If true, only show_valid mode is available (no raw data to share). */
  showValidOnly?: true;
  /** If true, THEY SHARE is read-only (contact's KYC status — can't be requested via API). */
  kycStatusOnly?: true;
}> = [
  { field: "phone",         label: "Phone Number",     short: "Phone",    icon: "smartphone"                          },
  { field: "email",         label: "Email Address",    short: "Email",    icon: "mail"                                },
  { field: "address",       label: "Home Address",     short: "Address",  icon: "home"                                },
  { field: "ssn",           label: "SSN Verification", short: "SSN",      icon: "lock",      showValidOnly: true      },
  { field: "gov_id",        label: "Gov ID Image",     short: "Gov ID",   icon: "id_card"                             },
  { field: "liveness",      label: "Liveness Scan",    short: "Liveness", icon: "face",      showValidOnly: true, kycStatusOnly: true },
  { field: "ofac",          label: "OFAC Check",       short: "OFAC",     icon: "gpp_good",  showValidOnly: true, kycStatusOnly: true },
  { field: "adverse_media", label: "Adverse Media",    short: "Media",    icon: "newspaper", showValidOnly: true, kycStatusOnly: true },
];



const AVATAR_COLORS = [
  "bg-blue-100 text-blue-600",
  "bg-emerald-100 text-emerald-600",
  "bg-amber-100 text-amber-600",
  "bg-rose-100 text-rose-600",
  "bg-purple-100 text-purple-600",
  "bg-cyan-100 text-cyan-600",
  "bg-orange-100 text-orange-600",
];

// Verification factor badges shown on the contact card
const VERIFICATION_BADGE_DEFS: Array<{ key: keyof ConnectionPerson; label: string; icon: string }> = [
  { key: "is_gov_id_verified",         label: "ID",          icon: "id_card"    },
  { key: "is_liveness_verified",       label: "Liveness",    icon: "face"       },
  { key: "is_phone_verified",          label: "Phone",       icon: "smartphone" },
  { key: "is_email_verified",          label: "Email",       icon: "mail"       },
  { key: "is_ssn_verified",            label: "SSN",         icon: "lock"       },
  { key: "is_ofac_verified",           label: "OFAC",        icon: "gpp_good"   },
  { key: "is_address_verified",        label: "Address",     icon: "home"       },
  { key: "is_adverse_media_verified",  label: "Media",       icon: "newspaper"  },
];

function getVerifiedBadges(person: ConnectionPerson | null) {
  if (!person) return [];
  return VERIFICATION_BADGE_DEFS.filter((d) => person[d.key] === true);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAvatarColor(initials: string): string {
  const idx = (initials.charCodeAt(0) + (initials.charCodeAt(1) || 0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

function extractContactInfo(person: ConnectionPerson | null): ContactInfo {
  const firstName = person?.first_name ?? "";
  const lastName  = person?.last_name  ?? "";
  const fullName  = `${firstName} ${lastName}`.trim() || "Unknown";
  const initials  = getInitials(firstName || "?", lastName);
  const avatarUrl = person?.avatar || null;
  const phone     = person?.phone || null;
  return { fullName, initials, avatarUrl, phone };
}

function formatSharingValue(
  field: SharableField,
  value: string | Record<string, string>,
): string {
  if (field === "address" && typeof value === "object") {
    const a = value as Record<string, string>;
    return [a.line1, a.city, a.state, a.postal_code].filter(Boolean).join(", ");
  }
  return String(value);
}

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86_400_000));
}

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

function AvatarCircle({
  name, initials, avatarUrl, size = 12,
}: {
  name: string; initials: string; avatarUrl: string | null; size?: number;
}) {
  const [imgError, setImgError] = useState(false);
  const color = getAvatarColor(initials);
  const dim   = `h-${size} w-${size}`;
  if (avatarUrl && !imgError) {
    return (
      <img src={avatarUrl} alt={name}
        className={cn(dim, "rounded-full border-4 border-primary-200 object-cover")}
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div className={cn(dim, "flex items-center justify-center rounded-full border-4 border-primary-200 text-sm font-black", color)} aria-hidden="true">
      {initials}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DualHeroHeader — used only for non-verified connections (flex, no grid needed)
// ---------------------------------------------------------------------------

function DualHeroHeader({
  me, contact, connection,
}: {
  me: MyInfo; contact: ContactInfo; connection: Connection;
}) {
  const isVerified = connection.status === "verified";
  const realEmail  = me.email && !me.email.endsWith("@uru.youareyou.io") ? me.email : undefined;
  // Only show contact-card previews for fields that are actively shared with this contact
  const phoneShared = connection.my_sharing?.phone?.status === "active";
  const emailShared = connection.my_sharing?.email?.status === "active";
  return (
    <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-4 pt-4 pb-3">
      <div className="flex items-stretch gap-3">
        <div className="flex flex-1 flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
          <AvatarCircle name={me.fullName} initials={me.initials} avatarUrl={me.avatarUrl} />
          <div className="w-full text-center">
            <p className="text-sm font-bold text-text-primary truncate">{me.fullName}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Are you Sharing?</p>
            {me.phone  && phoneShared && <p className="flex items-center justify-center gap-0.5 text-[10px] text-text-muted"><Icon name="smartphone" className="text-xs shrink-0 leading-none" />{me.phone}</p>}
            {realEmail && emailShared && <p className="flex items-center justify-center gap-0.5 text-[10px] text-text-muted"><Icon name="mail" className="text-xs shrink-0 leading-none" />{realEmail}</p>}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-center justify-center gap-1 py-2">
          <div className={cn("flex h-9 w-9 items-center justify-center rounded-full border-2 bg-white shadow-sm", isVerified ? "border-emerald-200" : "border-amber-200")}>
            <Icon name={isVerified ? "handshake" : "schedule"} filled={isVerified} className={cn("text-lg", isVerified ? "text-emerald-500" : "text-amber-500")} />
          </div>
          <span className={cn("text-[9px] font-bold uppercase tracking-wider", isVerified ? "text-emerald-600" : "text-amber-600")}>
            {isVerified ? "Trusted" : connection.status}
          </span>
        </div>
        <div className="relative flex flex-1 flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
          <button type="button" aria-label="Download contact card"
            className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full text-text-muted hover:bg-gray-100 hover:text-primary-500 transition-colors focus:outline-none">
            <Icon name="arrow_circle_down" className="text-base" />
          </button>
          <AvatarCircle name={contact.fullName} initials={contact.initials} avatarUrl={contact.avatarUrl} />
          <div className="w-full text-center">
            <p className="text-sm font-bold text-text-primary truncate pr-5">{contact.fullName}</p>
            {contact.phone && <p className="text-[11px] text-text-muted truncate">{contact.phone}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActionBtn — pill style
// ---------------------------------------------------------------------------

function ActionBtn({
  children, variant = "default", onClick, disabled, ariaLabel,
}: {
  children: React.ReactNode;
  variant?: "default" | "primary" | "grant" | "danger" | "request";
  onClick?: () => void; disabled?: boolean; ariaLabel?: string;
}) {
  const base = "shrink-0 rounded-full px-3.5 py-1 text-xs font-semibold transition-colors focus:outline-none disabled:opacity-50 min-w-[60px] text-center";
  const variants: Record<string, string> = {
    default: "border border-gray-200 bg-white text-text-secondary hover:bg-gray-50",
    primary: "bg-primary-500 text-white hover:bg-primary-600",
    grant:   "bg-primary-500 text-white hover:bg-primary-600",
    danger:  "border border-red-400 text-red-500 bg-transparent hover:bg-red-50",
    request: "border border-primary-500 text-primary-600 bg-transparent hover:bg-primary-50",
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={ariaLabel}
      className={cn(base, variants[variant])}>
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// GrantForm — single-line controls: [mode] · [expiry pills] · [Cancel][Send]
// The value preview is shown in the I SHARE cell above, not here.
// ---------------------------------------------------------------------------

// Fields that only support show_valid (no raw data to share).
const SHOW_VALID_ONLY: Set<SharableField> = new Set(["ssn", "liveness", "ofac", "adverse_media"]);

function GrantForm({
  field, mode, days, onModeChange, onDaysChange, onConfirm, onCancel,
}: {
  field: SharableField; mode: SharingMode; days: number;
  onModeChange: (m: SharingMode) => void;
  onDaysChange: (d: number) => void;
  onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <div className="absolute left-0 top-full z-20 w-full border border-primary-100 bg-white shadow-lg rounded-b-lg px-3 py-2">
      <div className="flex flex-col gap-1.5">

        {/* Row 1: mode toggle + Cancel/Send */}
        <div className="flex items-center gap-1.5">
          {!SHOW_VALID_ONLY.has(field) && (
            <>
              <button type="button" onClick={() => onModeChange("show_valid")}
                className={cn("rounded-full px-2.5 py-0.5 text-[10px] font-semibold transition-colors focus:outline-none",
                  mode === "show_valid" ? "bg-emerald-600 text-white" : "border border-gray-200 bg-white text-text-secondary hover:border-primary-300")}>
                Verified
              </button>
              <button type="button" onClick={() => onModeChange("share")}
                className={cn("rounded-full px-2.5 py-0.5 text-[10px] font-semibold transition-colors focus:outline-none",
                  mode === "share" ? "bg-primary-500 text-white" : "border border-gray-200 bg-white text-text-secondary hover:border-primary-300")}>
                Detailed
              </button>
            </>
          )}
          <div className="ml-auto flex gap-1.5">
            <button type="button" onClick={onCancel}
              className="rounded-full border border-gray-200 bg-white px-3 py-0.5 text-[10px] font-semibold text-text-secondary hover:bg-gray-50 focus:outline-none">
              Cancel
            </button>
            <button type="button" onClick={onConfirm}
              className="rounded-full bg-emerald-600 px-3 py-0.5 text-[10px] font-semibold text-white hover:bg-emerald-700 focus:outline-none">
              Send
            </button>
          </div>
        </div>

        {/* Row 2: expiry — only shown when Detailed mode is selected */}
        {mode === "share" && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-text-muted shrink-0">Expires:</span>
            {[7, 14, 30].map((d) => (
              <button key={d} type="button" onClick={() => onDaysChange(d)}
                className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors focus:outline-none",
                  days === d ? "bg-primary-500 text-white" : "border border-gray-200 bg-white text-text-secondary hover:border-primary-300")}>
                {d}d
              </button>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RequestForm — inline form shown in the THEY SHARE cell when requesting a field.
// Mirrors GrantForm: mode toggle (Verified / Detailed) + Cancel / Send.
// Note: mode is shown for UX clarity but not yet sent to the API (sharing_request
// only accepts {field}). The granter chooses how to share when they grant.
// ---------------------------------------------------------------------------

function RequestForm({
  field, mode, onModeChange, onConfirm, onCancel,
}: {
  field: SharableField; mode: SharingMode;
  onModeChange: (m: SharingMode) => void;
  onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <div className="absolute left-0 top-full z-20 w-full border border-primary-100 bg-white shadow-lg rounded-b-lg px-3 py-2">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          {field !== "ssn" && (
            <>
              <button type="button" onClick={() => onModeChange("show_valid")}
                className={cn("rounded-full px-2.5 py-0.5 text-[10px] font-semibold transition-colors focus:outline-none",
                  mode === "show_valid" ? "bg-emerald-600 text-white" : "border border-gray-200 bg-white text-text-secondary hover:border-primary-300")}>
                Verified
              </button>
              <button type="button" onClick={() => onModeChange("share")}
                className={cn("rounded-full px-2.5 py-0.5 text-[10px] font-semibold transition-colors focus:outline-none",
                  mode === "share" ? "bg-primary-500 text-white" : "border border-gray-200 bg-white text-text-secondary hover:border-primary-300")}>
                Detailed
              </button>
            </>
          )}
          <div className="ml-auto flex gap-1.5">
            <button type="button" onClick={onCancel}
              className="rounded-full border border-gray-200 bg-white px-3 py-0.5 text-[10px] font-semibold text-text-secondary hover:bg-gray-50 focus:outline-none">
              Cancel
            </button>
            <button type="button" onClick={onConfirm}
              className="rounded-full bg-primary-500 px-3 py-0.5 text-[10px] font-semibold text-white hover:bg-primary-600 focus:outline-none">
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TrustPanel — tab-based layout.
// Tab "them": full-width contact card + They Share factors accordion.
// Tab "you":  full-width You card + I Share factors accordion.
// ---------------------------------------------------------------------------

function TrustPanel({
  connection, me, contact, onConnectionUpdate,
}: {
  connection: Connection;
  me: MyInfo;
  contact: ContactInfo;
  onConnectionUpdate: (c: Connection) => void;
}) {
  const [activeTab,       setActiveTab]       = useState<"them" | "you">("them");
  const [themFactorsOpen, setThemFactorsOpen] = useState(false);
  const [youFactorsOpen,  setYouFactorsOpen]  = useState(false);
  const [manageOpen,      setManageOpen]      = useState(false);
  const [pendingActions, setPendingActions]   = useState<Set<string>>(new Set());
  const [grantTarget,   setGrantTarget]       = useState<{ field: SharableField; mode: SharingMode; days: number } | null>(null);
  const [requestTarget, setRequestTarget]     = useState<{ field: SharableField; mode: SharingMode } | null>(null);
  const [actionError, setActionError]         = useState<string | null>(null);
  const [kycLinkState, setKycLinkState]       = useState<Record<string, { sent: boolean }>>({});

  const isVerified = connection.status === "verified";
  const realEmail  = me.email && !me.email.endsWith("@uru.youareyou.io") ? me.email : undefined;
  const myData     = { phone: me.phone, email: realEmail };

  function isPending(key: string)  { return pendingActions.has(key); }
  function addPending(key: string) { setPendingActions((s) => new Set(s).add(key)); }
  function removePending(key: string) {
    setPendingActions((s) => { const n = new Set(s); n.delete(key); return n; });
  }

  async function refreshConnection() {
    const fresh = await getConnection(connection.id);
    onConnectionUpdate(fresh);
  }

  async function handleRequestKycLink(field: SharableField) {
    const key = `kyc-${field}`;
    addPending(key); setActionError(null);
    try {
      await requestDataSharing(connection.id, field);
      setKycLinkState((s) => ({ ...s, [field]: { sent: true } }));
    } catch {
      setActionError("Could not send request. Please try again.");
    } finally {
      removePending(key);
    }
  }

  async function handleGrant() {
    if (!grantTarget) return;
    const { field, mode, days } = grantTarget;
    const key = `grant-${field}`;
    addPending(key); setActionError(null); setGrantTarget(null);
    try {
      await grantDataSharing(connection.id, field, mode, days);
      const fresh = await getConnection(connection.id);
      onConnectionUpdate(fresh);
    }
    catch { setActionError("Share failed. Please try again."); }
    finally { removePending(key); }
  }

  async function handleRevoke(field: SharableField, direction: "outbound" | "inbound") {
    const key = `rev-${direction}-${field}`;
    addPending(key); setActionError(null);
    try   { await revokeDataSharing(connection.id, field, direction); await refreshConnection(); }
    catch { setActionError("Action failed. Please try again."); }
    finally { removePending(key); }
  }

  function openGrant(field: SharableField) {
    setGrantTarget({ field, mode: "show_valid", days: 7 });
  }

  async function handleRequest(field: SharableField) {
    const key = `req-${field}`; addPending(key); setActionError(null); setRequestTarget(null);
    try   { await requestDataSharing(connection.id, field); await refreshConnection(); }
    catch { setActionError("Action failed. Please try again."); }
    finally { removePending(key); }
  }

  function openRequest(field: SharableField) {
    setRequestTarget({ field, mode: "show_valid" });
  }

  // ── I SHARE cell ────────────────────────────────────────────────────────
  function MyCell({ field, label, myEntry, showValidOnly }: {
    field: SharableField; label: string; myEntry: MySharingEntry | null | undefined;
    showValidOnly?: boolean;
  }) {
    const myStatus    = myEntry?.status ?? "not_requested";
    const outboundKey = `rev-outbound-${field}`;
    const isGrantOpen = grantTarget?.field === field;

    // KYC gate: for showValidOnly fields, check if the user has completed this KYC factor.
    // me.kyc may be undefined while loading — treat as unverified (show disabled hint).
    const isKycGated  = showValidOnly && (me.kyc?.[field] === false || me.kyc === undefined);
    const isKycReady  = showValidOnly && me.kyc?.[field] === true;

    // When grant form is open: show a preview of what will be shared.
    if (isGrantOpen) {
      const grantMode   = grantTarget!.mode;
      const actualValue =
        grantMode === "share" && field === "phone" && myData.phone ? myData.phone
        : grantMode === "share" && field === "email" && myData.email ? myData.email
        : null;
      if (grantMode === "show_valid") {
        return (
          <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
            {label} <Icon name="check_circle" filled className="text-[9px] text-emerald-600" />
          </span>
        );
      }
      return (
        <span className="text-xs font-mono font-semibold text-primary-600 truncate leading-tight">
          {actualValue ?? label}
        </span>
      );
    }

    // KYC-gated fields where user hasn't completed the verification yet.
    if (isKycGated && (myStatus === "not_requested" || myStatus === "revoked" || myStatus === "expired")) {
      return (
        <div className="flex flex-col items-center gap-0.5">
          <ActionBtn variant="primary" disabled>Share</ActionBtn>
          <a href="/settings" className="text-[9px] font-semibold text-primary-500 hover:underline leading-tight text-center">
            Add Verification first
          </a>
        </div>
      );
    }

    // KYC-gated fields where user is verified but hasn't shared yet.
    if (isKycReady && (myStatus === "not_requested" || myStatus === "revoked" || myStatus === "expired")) {
      return <ActionBtn variant="primary" onClick={() => openGrant(field)}>Share</ActionBtn>;
    }

    if (myStatus === "not_requested" || myStatus === "revoked") {
      return <ActionBtn variant="primary" onClick={() => openGrant(field)}>Share</ActionBtn>;
    }
    if (myStatus === "pending") {
      return (
        <div className="flex flex-col gap-0.5">
          <ActionBtn variant="grant" onClick={() => openGrant(field)}>Grant</ActionBtn>
          <p className="text-[9px] text-amber-600 font-medium">Requested</p>
        </div>
      );
    }
    if (myStatus === "active") {
      return (
        <div className="flex flex-col gap-0.5">
          <ActionBtn variant="danger" onClick={() => handleRevoke(field, "outbound")}
            disabled={isPending(outboundKey)} ariaLabel={`Revoke ${label}`}>
            {isPending(outboundKey) ? <Icon name="progress_activity" className="text-xs animate-spin" /> : "Revoke"}
          </ActionBtn>
          {myEntry?.expires_at && daysUntil(myEntry.expires_at) !== null && (
            <p className="text-[9px] text-text-muted">{daysUntil(myEntry.expires_at)}d left</p>
          )}
        </div>
      );
    }
    if (myStatus === "expired") {
      return <ActionBtn variant="primary" onClick={() => openGrant(field)}>Re-share</ActionBtn>;
    }
    return null;
  }

  // ── THEY SHARE cell ──────────────────────────────────────────────────────
  function TheirCell({ field, label, icon, theirEntry, kycStatusOnly }: {
    field: SharableField; label: string; icon: string;
    theirEntry: TheirSharingEntry | null | undefined;
    /** If true, field is a KYC verification flag — not requestable via sharing_request. Show status only. */
    kycStatusOnly?: boolean;
  }) {
    const theirStatus   = theirEntry?.status ?? "not_requested";
    const requestKey    = `req-${field}`;
    const isRequestOpen = requestTarget?.field === field;

    if (theirStatus === "active") {
      if (theirEntry?.mode === "show_valid" && !kycStatusOnly) {
        // Verified badge only — allow upgrading to a detailed request.
        return (
          <div className="relative flex flex-col items-end gap-1">
            <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
              <Icon name="check_circle" filled className="text-sm text-emerald-500" /> VERIFIED
            </span>
            {isRequestOpen ? (
              <RequestForm
                field={field}
                mode={requestTarget!.mode}
                onModeChange={(m) => setRequestTarget((t) => t ? { ...t, mode: m } : null)}
                onConfirm={() => handleRequest(field)}
                onCancel={() => setRequestTarget(null)}
              />
            ) : (
              <ActionBtn variant="request" onClick={() => setRequestTarget({ field, mode: "share" })}
                ariaLabel={`Request detailed ${label}`}>
                Request detail
              </ActionBtn>
            )}
          </div>
        );
      }
      if (theirEntry?.mode === "show_valid" && kycStatusOnly) {
        return (
          <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
            <Icon name="check_circle" filled className="text-sm text-emerald-500" /> VERIFIED
          </span>
        );
      }
      if (theirEntry?.value) {
        return (
          <span className="text-xs font-medium text-text-primary break-all">{formatSharingValue(field, theirEntry.value)}</span>
        );
      }
      return <span className="text-xs font-medium text-emerald-600">Active</span>;
    }

    if (theirStatus === "pending") {
      return (
        <span className="flex items-center gap-1 text-xs font-semibold text-amber-600">
          <Icon name={icon} className="text-sm shrink-0" /> Requested
        </span>
      );
    }

    // KYC-status-only fields (liveness, ofac, adverse_media) can't use sharing_request.
    // Instead, generate a direct link requiring that KYC check — when the contact clicks it
    // MojoVerify runs the check and the existing connection deduplicates.
    if (kycStatusOnly) {
      const contactVerified = connection.connected_customer?.[`is_${field}_verified` as keyof ConnectionPerson] === true;
      if (contactVerified) {
        return (
          <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
            <Icon name="check_circle" filled className="text-sm text-emerald-500" /> Verified
          </span>
        );
      }
      if (kycLinkState[field]?.sent) {
        return (
          <span className="flex items-center gap-1 text-xs font-semibold text-amber-600">
            <Icon name={icon} className="text-sm shrink-0" /> Requested
          </span>
        );
      }
      return (
        <ActionBtn variant="request" onClick={() => handleRequestKycLink(field)}
          disabled={isPending(`kyc-${field}`)} ariaLabel={`Request ${label} verification`}>
          {isPending(`kyc-${field}`) ? <Icon name="progress_activity" className="text-xs animate-spin" /> : "Request"}
        </ActionBtn>
      );
    }

    // not_requested | revoked | expired → Request button.
    return (
      <>
        <ActionBtn variant="request" onClick={() => openRequest(field)}
          disabled={isPending(requestKey)} ariaLabel={`Request ${label}`}>
          {isPending(requestKey) ? <Icon name="progress_activity" className="text-xs animate-spin" /> : "Request"}
        </ActionBtn>
        {isRequestOpen && (
          <RequestForm
            field={field}
            mode={requestTarget!.mode}
            onModeChange={(m) => setRequestTarget((t) => t ? { ...t, mode: m } : null)}
            onConfirm={() => handleRequest(field)}
            onCancel={() => setRequestTarget(null)}
          />
        )}
      </>
    );
  }

  // Pending requested fields for the contact card preview
  const requestedFields = SHARABLE_FIELDS.filter(
    (f) => connection.their_sharing?.[f.field]?.status === "pending",
  );

  // Verified trust factor badges derived from connected_customer flags
  const contactVerifiedBadges = getVerifiedBadges(connection.connected_customer);

  // Actively shared fields — to show in the contact card after grant
  const activeTheirFields = SHARABLE_FIELDS.map((f) => {
    const entry = connection.their_sharing?.[f.field];
    if (entry?.status !== "active") return null;
    return { ...f, mode: entry.mode, value: entry.value };
  }).filter(Boolean) as Array<{ field: SharableField; label: string; short: string; icon: string; mode?: SharingMode; value?: string | Record<string, string> }>;

  // Fields I'm actively sharing — to show in the You card preview
  const activeMyFields = SHARABLE_FIELDS.map((f) => {
    const entry = connection.my_sharing?.[f.field];
    if (entry?.status !== "active") return null;
    // For phone/email prefer the known myData values; for other fields use entry.value
    const value: string | Record<string, string> | undefined =
      f.field === "phone" ? myData.phone
      : f.field === "email" ? myData.email
      : undefined;
    return { ...f, mode: entry.mode, value };
  }).filter(Boolean) as Array<{ field: SharableField; label: string; short: string; icon: string; mode?: SharingMode; value?: string | Record<string, string> }>;

  // Shared border utilities (desktop grid)
  const cb = "border-b border-gray-100";
  const cl = "border-l border-gray-100";

  return (
    <div className="flex-1 overflow-y-auto bg-white">

      {/* ══════════════════════════════════════════════════════════════════
          MOBILE — tab-based layout (hidden on md+)
      ══════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden">

      {/* ── Tab bar ──────────────────────────────────────────────────────── */}
      <div className="shrink-0 border-b border-gray-200 bg-gray-50">
        {/* Trust status badge */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="flex items-center gap-1.5">
            <div className={cn(
              "flex h-7 w-7 items-center justify-center rounded-full border-2 bg-white shadow-sm",
              isVerified ? "border-emerald-200" : "border-amber-200",
            )}>
              <Icon name={isVerified ? "handshake" : "schedule"} filled={isVerified}
                className={cn("text-sm", isVerified ? "text-emerald-500" : "text-amber-500")} />
            </div>
            <span className={cn(
              "text-[11px] font-bold uppercase tracking-wider",
              isVerified ? "text-emerald-600" : "text-amber-600",
            )}>
              {isVerified ? "Trusted" : connection.status}
            </span>
          </div>
        </div>
        {/* Tabs */}
        <div className="flex">
          <button type="button" onClick={() => setActiveTab("them")}
            className={cn(
              "flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors focus:outline-none border-b-2",
              activeTab === "them"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-text-muted hover:text-text-secondary",
            )}>
            Them
          </button>
          <button type="button" onClick={() => setActiveTab("you")}
            className={cn(
              "flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors focus:outline-none border-b-2",
              activeTab === "you"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-text-muted hover:text-text-secondary",
            )}>
            Your Connection
          </button>
        </div>
      </div>

      {/* ── Them tab ─────────────────────────────────────────────────────── */}
      {activeTab === "them" && (
        <div>
          {/* Contact card */}
          <div className="px-4 py-4">
            <div className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-4 shadow-sm">
              <AvatarCircle name={contact.fullName} initials={contact.initials} avatarUrl={contact.avatarUrl} size={16} />
              <div className="text-center">
                <p className="text-sm font-bold text-text-primary">{contact.fullName}</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Shared</p>
              </div>
              {(contactVerifiedBadges.length > 0 || activeTheirFields.length > 0 || requestedFields.length > 0) && (
                <div className="w-full space-y-1.5 pt-1 border-t border-gray-100">
                  {contactVerifiedBadges.map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2">
                      <Icon name="check_circle" filled className="text-sm text-emerald-500 shrink-0" />
                      <span className="text-xs font-medium text-text-primary">{label} Verified</span>
                    </div>
                  ))}
                  {activeTheirFields.map(({ field, icon, short, mode, value }) => (
                    <div key={field} className="flex items-center gap-2">
                      <Icon name={icon} className="text-sm text-text-muted shrink-0" />
                      <span className="text-xs text-text-primary">
                        {mode === "show_valid"
                          ? `${short} Verified`
                          : value
                            ? formatSharingValue(field, value)
                            : `${short} Active`}
                      </span>
                    </div>
                  ))}
                  {requestedFields.map(({ field, icon }) => (
                    <div key={field} className="flex items-center gap-2">
                      <Icon name={icon} className="text-sm text-amber-400 shrink-0" />
                      <span className="text-xs text-amber-600">Awaiting Verification</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Add/Remove Trust Factors accordion */}
          <button type="button" onClick={() => setThemFactorsOpen((o) => !o)}
            className="w-full flex items-center justify-between border-y border-gray-200 bg-gray-50/80 px-4 py-2.5 hover:bg-gray-100/80 transition-colors focus:outline-none">
            <span className="text-[11px] font-black uppercase tracking-widest text-text-muted">
              Add/Remove Trust Factors
            </span>
            <Icon name={themFactorsOpen ? "expand_less" : "expand_more"} className="text-base text-text-muted" />
          </button>

          <div className={cn("overflow-hidden transition-all duration-200 ease-out", themFactorsOpen ? "max-h-[800px]" : "max-h-0")}>
            <div>
              {SHARABLE_FIELDS.map(({ field, label, short, icon, kycStatusOnly }) => {
                const theirEntry = connection.their_sharing?.[field] as TheirSharingEntry | null | undefined;
                return (
                  <div key={field} className="relative flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Icon name={icon} className="text-base text-text-muted shrink-0" />
                      <span className="text-xs text-text-secondary">{short}</span>
                    </div>
                    <div className="shrink-0">
                      <TheirCell field={field} label={label} icon={icon} theirEntry={theirEntry} kycStatusOnly={kycStatusOnly} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Your Connection tab ───────────────────────────────────────────── */}
      {activeTab === "you" && (
        <div>
          {/* Your card */}
          <div className="px-4 py-4">
            <div className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-4 shadow-sm">
              <AvatarCircle name={me.fullName} initials={me.initials} avatarUrl={me.avatarUrl} size={16} />
              <div className="text-center">
                <p className="text-sm font-bold text-text-primary">{me.fullName}</p>
                <p className="text-[9px] font-bold uppercase tracking-widest text-text-muted">Are you Sharing?</p>
              </div>
              {activeMyFields.length > 0 && (
                <div className="flex flex-wrap justify-center gap-1.5 pt-0.5">
                  {activeMyFields.map(({ field, icon, short, mode, value }) => (
                    <span key={field} className="flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                      <Icon name={mode === "show_valid" ? "check_circle" : icon} filled={mode === "show_valid"} style={{ fontSize: 11 }} />
                      {mode === "show_valid"
                        ? `${short} Verified`
                        : value
                          ? formatSharingValue(field, value)
                          : `${short} Shared`}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action error */}
          {actionError && (
            <div className="mx-4 mb-3 rounded-lg bg-red-50 border border-red-200 px-4 py-2">
              <p className="text-center text-xs font-semibold text-error">{actionError}</p>
            </div>
          )}

          {/* Add/Remove Trust Factors accordion */}
          <button type="button" onClick={() => setYouFactorsOpen((o) => !o)}
            className="w-full flex items-center justify-between border-y border-gray-200 bg-gray-50/80 px-4 py-2.5 hover:bg-gray-100/80 transition-colors focus:outline-none">
            <span className="text-[11px] font-black uppercase tracking-widest text-text-muted">
              Add/Remove Trust Factors
            </span>
            <Icon name={youFactorsOpen ? "expand_less" : "expand_more"} className="text-base text-text-muted" />
          </button>

          <div className={cn("overflow-hidden transition-all duration-200 ease-out", youFactorsOpen ? "max-h-[600px]" : "max-h-0")}>
            <div>
              {SHARABLE_FIELDS.map(({ field, label, short, icon, showValidOnly }, i) => {
                const myEntry    = connection.my_sharing?.[field] as MySharingEntry | null | undefined;
                const rowBorder  = i < SHARABLE_FIELDS.length - 1 ? "border-b border-gray-100" : "";
                const isGrantOpen = grantTarget?.field === field;
                return (
                  <div key={field} className={cn("relative flex items-center gap-3 px-4 py-3", rowBorder)}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Icon name={icon} className="text-base text-text-muted shrink-0" />
                      <span className="text-xs text-text-secondary">{short}</span>
                    </div>
                    <div className="shrink-0">
                      <MyCell field={field} label={label} myEntry={myEntry} showValidOnly={showValidOnly} />
                    </div>
                    {isGrantOpen && (
                      <GrantForm
                        field={field}
                        mode={grantTarget!.mode}
                        days={grantTarget!.days}
                        onModeChange={(m) => setGrantTarget((t) => t ? { ...t, mode: m } : null)}
                        onDaysChange={(d) => setGrantTarget((t) => t ? { ...t, days: d } : null)}
                        onConfirm={handleGrant}
                        onCancel={() => setGrantTarget(null)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      </div>{/* end md:hidden mobile layout */}

      {/* ══════════════════════════════════════════════════════════════════
          DESKTOP — 4-col grid layout (hidden below md)
      ══════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:grid grid-cols-[60px_1fr_1fr_60px] px-2">

        {/* ── Header row ───────────────────────────────────────────────── */}
        <div className="bg-gray-50 border-b border-gray-200" />

        {/* Col 2 — You card */}
        <div className="relative bg-gray-50 border-b border-gray-200 py-3 pl-2 pr-8">
          <div className="flex flex-col items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-2.5 py-2.5 shadow-sm h-full">
            <AvatarCircle name={me.fullName} initials={me.initials} avatarUrl={me.avatarUrl} size={12} />
            <div className="w-full text-center">
              <p className="text-xs font-bold text-text-primary leading-tight truncate">{me.fullName}</p>
              <p className="text-[9px] font-bold uppercase tracking-widest text-text-muted">Are you Sharing?</p>
            </div>
            {activeMyFields.length > 0 && (
              <div className="flex flex-wrap justify-center gap-1 pt-0.5">
                {activeMyFields.map(({ field, icon, short, mode, value }) => (
                  <span key={field} className="flex items-center gap-0.5 rounded-full bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">
                    <Icon name={mode === "show_valid" ? "check_circle" : icon} filled={mode === "show_valid"} style={{ fontSize: 10 }} />
                    {mode === "show_valid"
                      ? `${short} Verified`
                      : value
                        ? formatSharingValue(field, value)
                        : `${short} Shared`}
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* Trust icon straddles col-2/col-3 boundary */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 flex flex-col items-center gap-0.5">
            <div className={cn("flex h-9 w-9 items-center justify-center rounded-full border-2 bg-white shadow-sm",
              isVerified ? "border-emerald-200" : "border-amber-200")}>
              <Icon name={isVerified ? "handshake" : "schedule"} filled={isVerified}
                className={cn("text-lg", isVerified ? "text-emerald-500" : "text-amber-500")} />
            </div>
            <span className={cn("text-[8px] font-bold uppercase tracking-wider",
              isVerified ? "text-emerald-600" : "text-amber-600")}>
              {isVerified ? "Trusted" : connection.status}
            </span>
          </div>
        </div>

        {/* Col 3 — Contact card */}
        <div className="relative bg-gray-50 border-b border-gray-200 py-3 pl-8 pr-2">
          <div className="flex flex-col items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-2.5 py-2.5 shadow-sm h-full">
            <AvatarCircle name={contact.fullName} initials={contact.initials} avatarUrl={contact.avatarUrl} size={12} />
            <p className="text-xs font-bold text-text-primary leading-tight truncate w-full text-center">{contact.fullName}</p>
            <p className="text-[9px] font-bold uppercase tracking-widest text-text-muted text-center">Shared</p>
            {(contactVerifiedBadges.length > 0 || activeTheirFields.length > 0 || requestedFields.length > 0) && (
              <div className="w-full space-y-1 pt-1 border-t border-gray-100">
                {contactVerifiedBadges.map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-1.5">
                    <Icon name="check_circle" filled className="text-xs text-emerald-500 shrink-0" />
                    <span className="text-[10px] font-medium text-text-primary">{label} Verified</span>
                  </div>
                ))}
                {activeTheirFields.map(({ field, icon, short, mode, value }) => (
                  <div key={field} className="flex items-center gap-1.5">
                    <Icon name={icon} className="text-xs text-text-muted shrink-0" />
                    <span className="text-[10px] text-text-primary">
                      {mode === "show_valid"
                        ? `${short} Verified`
                        : value
                          ? formatSharingValue(field, value)
                          : `${short} Active`}
                    </span>
                  </div>
                ))}
                {requestedFields.map(({ field, icon }) => (
                  <div key={field} className="flex items-center gap-1.5">
                    <Icon name={icon} className="text-xs text-amber-400 shrink-0" />
                    <span className="text-[10px] text-amber-600">Awaiting Verification</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Col 4 — right spacer */}
        <div className="bg-gray-50 border-b border-gray-200" />

        {/* ── Add/Remove Trust Factors bar ─────────────────────────────── */}
        <button type="button" onClick={() => setManageOpen((o) => !o)}
          className="col-span-4 flex items-center justify-between border-b border-gray-200 bg-gray-50/80 px-4 py-2.5 hover:bg-gray-100/80 transition-colors focus:outline-none">
          <span className="text-[11px] font-black uppercase tracking-widest text-text-muted">
            Add/Remove Trust Factors
          </span>
          <Icon name={manageOpen ? "expand_less" : "expand_more"} className="text-base text-text-muted" />
        </button>

        {/* ── Sharing table (animated) ──────────────────────────────────── */}
        <div className={cn("col-span-4 overflow-hidden transition-all duration-200 ease-out", manageOpen ? "max-h-[1200px]" : "max-h-0")}>
          <div className="grid grid-cols-[60px_1fr_1fr_60px]">
            {actionError && (
              <div className="col-span-4 bg-red-50 border-b border-red-200 px-4 py-1.5">
                <p className="text-center text-xs font-semibold text-error">{actionError}</p>
              </div>
            )}

            {SHARABLE_FIELDS.map(({ field, label, short, icon, showValidOnly, kycStatusOnly }) => {
              const myEntry    = connection.my_sharing?.[field]    as MySharingEntry    | null | undefined;
              const theirEntry = connection.their_sharing?.[field] as TheirSharingEntry | null | undefined;
              const isGrantOpen = grantTarget?.field === field;

              return (
                <Fragment key={field}>
                  {/* Col 1 — icon + short label */}
                  <div className={cn("flex flex-col items-center justify-center gap-0.5 px-1 py-3", cb)}>
                    <Icon name={icon} className="text-base text-text-muted" />
                    <p className="text-[9px] font-medium text-text-muted leading-none text-center">{short}</p>
                  </div>

                  {/* Col 2 — I SHARE */}
                  <div className={cn("relative flex items-center justify-center px-3 py-3", cb, cl)}>
                    <MyCell field={field} label={label} myEntry={myEntry} showValidOnly={showValidOnly} />
                    {isGrantOpen && (
                      <GrantForm
                        field={field}
                        mode={grantTarget!.mode}
                        days={grantTarget!.days}
                        onModeChange={(m) => setGrantTarget((t) => t ? { ...t, mode: m } : null)}
                        onDaysChange={(d) => setGrantTarget((t) => t ? { ...t, days: d } : null)}
                        onConfirm={handleGrant}
                        onCancel={() => setGrantTarget(null)}
                      />
                    )}
                  </div>

                  {/* Col 3 — THEY SHARE */}
                  <div className={cn("relative flex items-center justify-center px-3 py-3", cb, cl)}>
                    <TheirCell field={field} label={label} icon={icon} theirEntry={theirEntry} kycStatusOnly={kycStatusOnly} />
                  </div>

                  {/* Col 4 — right spacer */}
                  <div className={cn(cb)} />
                </Fragment>
              );
            })}
          </div>
        </div>

      </div>{/* end desktop grid */}

    </div>
  );
}

// ---------------------------------------------------------------------------
// Non-verified content
// ---------------------------------------------------------------------------

function NonVerifiedContent({ connection }: { connection: Connection }) {
  const message: Record<string, string> = {
    pending:   "This connection is awaiting mutual verification.",
    denied:    "This connection was declined.",
    cancelled: "This connection was cancelled.",
  };
  return (
    <div className="flex-1 overflow-y-auto px-6 py-8 text-center">
      <p className="text-sm text-text-secondary">
        {message[connection.status] ?? `Connection status: ${connection.status}`}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <>
      {/* Mobile skeleton — tab bar + single card */}
      <div className="md:hidden">
        <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-4 pt-3 pb-0">
          <div className="flex justify-center pb-2">
            <div className="h-5 w-24 animate-pulse rounded-full bg-gray-200" />
          </div>
          <div className="flex gap-4 pb-3">
            <div className="flex-1 h-3 animate-pulse rounded bg-gray-200" />
            <div className="flex-1 h-3 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
        <div className="px-4 py-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col items-center gap-3">
            <div className="h-16 w-16 animate-pulse rounded-full bg-gray-200" />
            <div className="h-3 w-24 animate-pulse rounded bg-gray-200" />
            <div className="h-2.5 w-16 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
        <div className="px-4 space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-10 animate-pulse rounded-lg bg-gray-100" />
          ))}
        </div>
      </div>

      {/* Desktop skeleton — dual card header + field rows */}
      <div className="hidden md:block">
        <div className="grid grid-cols-[60px_1fr_1fr_60px] border-b border-gray-200 bg-gray-50">
          <div className="p-2" />
          <div className="py-3 pl-2 pr-8">
            <div className="rounded-xl border border-gray-200 bg-white p-2.5 flex flex-col items-center gap-2">
              <div className="h-12 w-12 animate-pulse rounded-full bg-gray-200" />
              <div className="h-3 w-16 animate-pulse rounded bg-gray-200" />
            </div>
          </div>
          <div className="py-3 pl-8 pr-2">
            <div className="rounded-xl border border-gray-200 bg-white p-2.5 flex flex-col items-center gap-2">
              <div className="h-12 w-12 animate-pulse rounded-full bg-gray-200" />
              <div className="h-3 w-20 animate-pulse rounded bg-gray-200" />
            </div>
          </div>
          <div className="p-2" />
        </div>
        <div className="flex-1 p-4 space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-gray-100" />
          ))}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function ErrorContent({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
        <Icon name="error" className="text-3xl text-error" />
      </div>
      <h3 className="text-base font-semibold text-text-primary">Unable to load contact</h3>
      <p className="mt-1 max-w-xs text-sm text-text-secondary">{message}</p>
      <button onClick={onRetry}
        className="mt-4 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600 focus:outline-none">
        Try again
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Resolved content
// ---------------------------------------------------------------------------

function ResolvedContent({
  connection: initialConnection, contact, me, onClose, onCancelled,
}: {
  connection: Connection; contact: ContactInfo; me: MyInfo;
  onClose: () => void; onCancelled?: (pk: number) => void;
}) {
  const [connection,  setConnection]  = useState<Connection>(initialConnection);
  const [copied,      setCopied]      = useState(false);
  const [cancelling,  setCancelling]  = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const { isInAddressBook, addToContacts } = useContactMatch();
  const contactPhone = contact.phone ?? null;
  const inContacts = isInAddressBook(contactPhone);

  const isVerified = connection.status === "verified";
  const canCancel  = connection.status === "pending" && connection.direction === "outgoing";
  const shareBase  = (import.meta.env.VITE_APP_URL ?? "https://youareyou.io").replace(/^https?:\/\//, "");
  const linkUrl    = connection.link
    ? (() => { try { return `https://${shareBase}${new URL(connection.link).pathname}`; } catch { return null; } })()
    : null;

  const handleCancel = useCallback(async () => {
    setCancelling(true); setCancelError(null);
    try   { await cancelConnection(connection.id); onCancelled?.(connection.id); onClose(); }
    catch { setCancelError("Failed to cancel. Please try again."); setCancelling(false); }
  }, [connection.id, onCancelled, onClose]);

  const handleCopyLink = useCallback(async () => {
    if (!linkUrl) return;
    try { await navigator.clipboard.writeText(linkUrl); }
    catch {
      const el = document.createElement("textarea");
      el.value = linkUrl; el.style.cssText = "position:fixed;opacity:0";
      document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [linkUrl]);

  return (
    <>
      {isVerified ? (
        <TrustPanel connection={connection} me={me} contact={contact} onConnectionUpdate={setConnection} />
      ) : (
        <>
          <DualHeroHeader me={me} contact={contact} connection={connection} />
          <NonVerifiedContent connection={connection} />
        </>
      )}

      <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3 space-y-2 md:px-6">
        {linkUrl && connection.status === "pending" && (
          <button onClick={handleCopyLink}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-600 focus:outline-none">
            <Icon name={copied ? "check_circle" : "content_copy"} className="text-lg" />
            {copied ? "Copied!" : "Copy Link to Share"}
          </button>
        )}
        {canCancel && (
          <>
            {cancelError && <p className="text-center text-xs text-error">{cancelError}</p>}
            <button onClick={handleCancel} disabled={cancelling}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-error hover:bg-red-100 disabled:opacity-50 focus:outline-none">
              {cancelling ? <Icon name="progress_activity" className="text-lg animate-spin" /> : <Icon name="cancel" className="text-lg" />}
              {cancelling ? "Cancelling…" : "Cancel Connection"}
            </button>
          </>
        )}
        {/* Call / Message — visible on mobile (web + native); hidden on desktop */}
        {contactPhone && (
          <div className="flex gap-2 md:hidden">
            <a
              href={`tel:${contactPhone}`}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-text-primary hover:bg-gray-50 focus:outline-none"
              aria-label={`Call ${contact.fullName}`}
            >
              <Icon name="call" className="text-lg text-success" />
              Call
            </a>
            <a
              href={`sms:${contactPhone}`}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-text-primary hover:bg-gray-50 focus:outline-none"
              aria-label={`Message ${contact.fullName}`}
            >
              <Icon name="chat_bubble" className="text-lg text-primary-500" />
              Message
            </a>
          </div>
        )}

        {/* Add to Contacts — native iOS only, and only when not already in contacts */}
        {isNative && contactPhone && !inContacts && (
          <button
            onClick={() =>
              addToContacts({
                firstName: contact.fullName.split(' ')[0] ?? contact.fullName,
                lastName:  contact.fullName.split(' ').slice(1).join(' '),
                phone:     contactPhone,
              })
            }
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-text-primary hover:bg-gray-50 focus:outline-none"
          >
            <Icon name="person_add" className="text-lg text-primary-500" />
            Add to Contacts
          </button>
        )}

        <button onClick={onClose}
          className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-text-primary hover:bg-gray-50 focus:outline-none">
          Close
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// ContactDetailModal — main component
// ---------------------------------------------------------------------------

export default function ContactDetailModal({
  connectionId, onClose, onCancelled, onSharingLoaded,
}: ContactDetailModalProps) {
  const { user } = useAuth();

  const [state,        setState]        = useState<ModalState>({ kind: "loading" });
  const [myKyc,        setMyKyc]        = useState<Partial<Record<string, boolean>> | undefined>(undefined);

  const me: MyInfo = {
    fullName:  user ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || "You" : "You",
    initials:  user ? getInitials(user.first_name ?? "", user.last_name ?? "") : "?",
    avatarUrl: user ? resolveAvatarUrl(user.avatar) : null,
    phone:     user?.phone_number || undefined,
    email:     user?.email        || undefined,
    kyc:       myKyc,
  };
  const [fetchAttempt, setFetchAttempt] = useState(0);
  const modalRef         = useRef<HTMLDivElement>(null);
  const originalOverflow = useRef<string>("");

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    Promise.all([getConnection(connectionId), getUruMe()]).then(
      ([connection, uruMe]) => {
        if (cancelled) return;
        setState({ kind: "resolved", connection, contact: extractContactInfo(connection.connected_customer) });
        setMyKyc({
          liveness:      uruMe.kyc.is_liveness_verified,
          ofac:          uruMe.kyc.is_ofac_verified,
          adverse_media: uruMe.kyc.is_adverse_media_verified,
          ssn:           uruMe.kyc.is_ssn_verified,
          gov_id:        uruMe.kyc.is_gov_id_verified,
          phone:         uruMe.kyc.is_phone_verified,
          email:         uruMe.kyc.is_email_verified,
          address:       uruMe.kyc.is_address_verified,
        });
        if (onSharingLoaded) {
          const hasActive =
            (connection.my_sharing   != null && (Object.values(connection.my_sharing)   as (MySharingEntry   | null)[]).some((e) => e?.status === "active")) ||
            (connection.their_sharing != null && (Object.values(connection.their_sharing) as (TheirSharingEntry | null)[]).some((e) => e?.status === "active"));
          onSharingLoaded(connectionId, hasActive);
        }
      },
      (err: unknown) => {
        if (cancelled) return;
        let message = "This connection could not be loaded. Please try again.";
        if (err instanceof Error) {
          if (err.message.includes("404") || err.message.includes("not found"))
            message = "This connection doesn't exist or you don't have access to it.";
          else if (err.message.includes("network") || err.message.includes("Network"))
            message = "Unable to reach the server. Check your connection.";
        }
        setState({ kind: "error", message });
      },
    );
    return () => { cancelled = true; };
  }, [connectionId, fetchAttempt]);

  useEffect(() => {
    originalOverflow.current = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = originalOverflow.current; };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key !== "Tab" || !modalRef.current) return;
      const els = modalRef.current.querySelectorAll<HTMLElement>(
        'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])',
      );
      if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const t = setTimeout(() => modalRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const handleRetry = useCallback(() => setFetchAttempt((n) => n + 1), []);
  const headerTitle = state.kind === "resolved" ? state.contact.fullName.toUpperCase() : "CONTACT";

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center" role="presentation">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        ref={modalRef}
        role="dialog" aria-modal="true" aria-labelledby="contact-detail-title" tabIndex={-1}
        className="relative z-10 flex max-h-[100dvh] w-full flex-col bg-white shadow-2xl h-full max-w-[430px] md:h-auto md:max-h-[90vh] md:max-w-2xl md:rounded-2xl overflow-hidden focus:outline-none"
      >
        <div className="flex shrink-0 items-center border-b border-gray-200 bg-white px-4 py-3">
          <button onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-gray-100 hover:text-text-primary focus:outline-none"
            aria-label="Close">
            <Icon name="close" className="text-xl" />
          </button>
          <h2 id="contact-detail-title"
            className="flex-1 pr-8 text-center text-xs font-bold uppercase tracking-widest text-text-secondary">
            {headerTitle}
          </h2>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden">
          {state.kind === "loading"  && <LoadingSkeleton />}
          {state.kind === "error"    && <ErrorContent message={state.message} onRetry={handleRetry} />}
          {state.kind === "resolved" && (
            <ResolvedContent connection={state.connection} contact={state.contact}
              me={me} onClose={onClose} onCancelled={onCancelled} />
          )}
        </div>
      </div>
    </div>
  );
}
